import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  useToast,
} from "../../components/ui";
import type { Column } from "../../components/ui";
import {
  aiProviderService,
  type AiProvider,
  type AiProviderListMeta,
  type AiProviderPayload,
} from "../../services/ai-provider.service";
import { validationErrors } from "../../services/http";

const PROVIDER_LABELS: Record<string, string> = {
  openai_compatible: "API compatível com OpenAI",
  ollama: "Ollama (modelo local)",
};

const ROLE_LABELS: Record<string, string> = {
  primary: "Principal",
  fallback: "Fallback",
  secondary: "Secundário",
};

interface FormState extends AiProviderPayload {
  id?: string;
}

const EMPTY_FORM: FormState = {
  provider: "openai_compatible",
  label: "",
  base_url: "",
  api_key: "",
  default_model: "",
  role: "primary",
  is_active: true,
};

/**
 * Configurações > Inteligência Artificial.
 * A chave de API é enviada uma única vez e nunca volta do backend.
 */
export default function AiProvidersPage() {
  const toast = useToast();
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [meta, setMeta] = useState<AiProviderListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AiProvider | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await aiProviderService.list();
      setProviders(response.data);
      setMeta(response.meta);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Falha ao carregar providers.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const providerOptions = useMemo(
    () =>
      (meta?.supported_providers ?? []).map((item) => ({
        value: item.key,
        label: PROVIDER_LABELS[item.key] ?? item.key,
      })),
    [meta],
  );

  const modelOptions = useMemo(() => {
    const current = providers.find((item) => item.id === form?.id);
    return (current?.models ?? []).map((model) => ({
      value: model.id,
      label: model.label ?? model.id,
    }));
  }, [providers, form?.id]);

  function openCreate() {
    setErrors({});
    setForm({
      ...EMPTY_FORM,
      base_url: meta?.supported_providers[0]?.default_base_url ?? "",
    });
  }

  function openEdit(provider: AiProvider) {
    setErrors({});
    setForm({
      id: provider.id,
      provider: provider.provider,
      label: provider.label ?? "",
      base_url: provider.base_url ?? "",
      api_key: "",
      default_model: provider.default_model ?? "",
      role: provider.role,
      is_active: provider.is_active,
    });
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setErrors({});

    const payload: AiProviderPayload = {
      provider: form.provider,
      label: form.label || null,
      base_url: form.base_url || null,
      default_model: form.default_model || null,
      role: form.role,
      is_active: form.is_active ?? true,
      ...(form.api_key ? { api_key: form.api_key } : {}),
    };

    try {
      if (form.id) {
        await aiProviderService.update(form.id, payload);
      } else {
        await aiProviderService.create(payload);
      }
      toast.success("Provider salvo.");
      setForm(null);
      await load();
    } catch (error) {
      setErrors(validationErrors(error));
      toast.error(error instanceof Error ? error.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  }

  async function test(provider: AiProvider) {
    setTesting(provider.id);
    try {
      const result = await aiProviderService.test(provider.id);
      toast.success(result.message);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Falha no teste de conexão.");
    } finally {
      setTesting(null);
      await load();
    }
  }

  async function toggle(provider: AiProvider) {
    try {
      await aiProviderService.toggle(provider.id, !provider.is_active);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível alterar o status.");
    }
  }

  async function remove() {
    if (!removing) return;
    try {
      await aiProviderService.remove(removing.id);
      toast.success("Provider removido.");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível remover.");
    } finally {
      setRemoving(null);
    }
  }

  const columns: Column<AiProvider>[] = [
    {
      key: "provider",
      header: "Provider",
      render: (row) => (
        <div>
          <div className="font-medium">{row.label || PROVIDER_LABELS[row.provider] || row.provider}</div>
          <div className="text-xs text-muted">{row.base_url}</div>
        </div>
      ),
    },
    {
      key: "model",
      header: "Modelo padrão",
      hideOnMobile: true,
      render: (row) => <span className="text-sm">{row.default_model ?? "—"}</span>,
    },
    {
      key: "role",
      header: "Papel",
      hideOnMobile: true,
      render: (row) => (
        <Badge tone={row.role === "primary" ? "info" : row.role === "fallback" ? "warn" : "neutral"}>
          {ROLE_LABELS[row.role] ?? row.role}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <div className="flex flex-col gap-1">
          <Badge tone={row.is_active ? "ok" : "neutral"} dot>
            {row.is_active ? "Ativo" : "Inativo"}
          </Badge>
          {row.last_test.status && (
            <span className="text-xs text-muted">
              Último teste: {row.last_test.status === "ok" ? "OK" : "falhou"}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "key",
      header: "Chave",
      hideOnMobile: true,
      render: (row) => (
        <span className="text-xs text-muted">
          {row.has_api_key ? (row.api_key_preview ?? "••••") : "não usa chave"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Ações",
      align: "right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => void test(row)} disabled={testing === row.id}>
            {testing === row.id ? "Testando…" : "Testar"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void toggle(row)}>
            {row.is_active ? "Desativar" : "Ativar"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
            Editar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRemoving(row)}>
            Remover
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Inteligência Artificial"
        description="Providers plugáveis: qualquer API compatível com OpenAI ou modelos locais via Ollama. As chaves ficam criptografadas no backend e nunca são exibidas."
        actions={<Button onClick={openCreate}>Adicionar provider</Button>}
      />

      <Card>
        {loading ? (
          <SkeletonRows rows={4} />
        ) : providers.length === 0 ? (
          <EmptyState
            title="Nenhum provider configurado"
            description="Cadastre um provider principal e, se quiser, um fallback usado apenas em falhas temporárias."
            action={<Button onClick={openCreate}>Adicionar provider</Button>}
          />
        ) : (
          <Table columns={columns} rows={providers} rowKey={(row) => row.id} />
        )}
      </Card>

      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        title={form?.id ? "Editar provider" : "Adicionar provider"}
        description="A chave é armazenada criptografada e nunca retorna ao navegador."
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>
              Cancelar
            </Button>
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? "Salvando…" : "Salvar"}
            </Button>
          </>
        }
      >
        {form && (
          <div className="grid gap-4">
            <Select
              label="Tipo de provider"
              options={providerOptions}
              value={form.provider}
              error={errors.provider?.[0]}
              onChange={(event) => {
                const provider = event.target.value;
                const suggestion = meta?.supported_providers.find((item) => item.key === provider);
                setForm({ ...form, provider, base_url: suggestion?.default_base_url ?? "" });
              }}
            />
            <Input
              label="Nome"
              placeholder="Ex.: OpenAI produção"
              value={form.label ?? ""}
              error={errors.label?.[0]}
              onChange={(event) => setForm({ ...form, label: event.target.value })}
            />
            <Input
              label="URL base"
              placeholder="https://api.exemplo.com/v1"
              value={form.base_url ?? ""}
              error={errors.base_url?.[0]}
              onChange={(event) => setForm({ ...form, base_url: event.target.value })}
            />
            <Input
              label="Chave de API"
              type="password"
              placeholder={form.id ? "Deixe em branco para manter a chave atual" : "Opcional em modelos locais"}
              value={form.api_key ?? ""}
              error={errors.api_key?.[0]}
              onChange={(event) => setForm({ ...form, api_key: event.target.value })}
            />
            {modelOptions.length > 0 ? (
              <Select
                label="Modelo padrão"
                placeholder="Selecione um modelo"
                options={modelOptions}
                value={form.default_model ?? ""}
                error={errors.default_model?.[0]}
                onChange={(event) => setForm({ ...form, default_model: event.target.value })}
              />
            ) : (
              <Input
                label="Modelo padrão"
                hint="Teste a conexão para listar os modelos disponíveis."
                value={form.default_model ?? ""}
                error={errors.default_model?.[0]}
                onChange={(event) => setForm({ ...form, default_model: event.target.value })}
              />
            )}
            <Select
              label="Papel na cadeia de execução"
              hint="O fallback só é usado em falhas temporárias do principal."
              options={(meta?.roles ?? []).map((role) => ({
                value: role,
                label: ROLE_LABELS[role] ?? role,
              }))}
              value={form.role ?? "secondary"}
              onChange={(event) => setForm({ ...form, role: event.target.value })}
            />
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        title="Remover provider"
        description="As execuções já registradas permanecem no histórico."
        destructive
        confirmLabel="Remover"
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
